import { useCallback, useEffect, useState } from "react";
import { listGhlConnections, updateGhlConnection } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, Link2, RefreshCw } from "lucide-react";

const SOURCE = {
  own: { label: "Own workflow", cls: "text-emerald-300 bg-emerald-500/10" },
  city: { label: "Shared (same city)", cls: "text-sky-300 bg-sky-500/10" },
  country: { label: "Shared (same country)", cls: "text-sky-300 bg-sky-500/10" },
  default: { label: "Shared (LIFTR default)", cls: "text-sky-300 bg-sky-500/10" },
  missing: { label: "Not set up", cls: "text-amber-300 bg-amber-500/10" },
};

export default function GhlConnections() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await listGhlConnections());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load GoHighLevel connections");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  return (
    <div className="space-y-6" data-testid="ghl-connections-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Admin · Messaging</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">GoHighLevel</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Every gym sends its WhatsApp and email through its own GoHighLevel account. Each gym needs its GHL location ID,
            plus the inbound-webhook URL of each workflow (one per kind of message). A webhook is only accepted from the
            gym&apos;s own GHL account, so messages always go out from the right gym&apos;s number. Bills use the same
            location ID plus an API token, set under Settings → Billing details.
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {s && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${s.connected === s.active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}
          data-testid="ghl-summary">
          {s.connected === s.active ? `All ${s.active} active gyms are connected.`
            : `${s.connected} of ${s.active} active gyms are fully connected.${s.missing_location ? ` ${s.missing_location} still need their GHL location ID.` : ""}`}
        </div>
      )}

      <div className="card-surface overflow-x-auto">
        {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : (
          <table className="w-full text-sm" data-testid="ghl-table">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3">Gym</th>
                <th className="px-4 py-3">GHL location</th>
                {data.purposes.map((p) => <th key={p.key} className="px-4 py-3">{p.label}</th>)}
                <th className="px-4 py-3">Bill emails (API token)</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.facilities.map((f) => (
                <tr key={f.id} className={`border-b border-[#161616] last:border-0 ${f.active ? "" : "opacity-50"}`} data-testid={`ghl-row-${f.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium text-zinc-100">
                      {f.connected ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}
                      {f.name}
                    </div>
                    <div className="text-[11px] text-zinc-500">{[f.city, f.country].filter(Boolean).join(", ")}{f.active ? "" : " · inactive"}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-300">{f.location_id || <span className="text-amber-300">Missing</span>}</td>
                  {data.purposes.map((p) => {
                    const st = f.purposes[p.key];
                    const meta = SOURCE[st.source] || SOURCE.missing;
                    return (
                      <td key={p.key} className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>{meta.label}</span>
                        {st.via && st.source !== "own" && <div className="mt-1 text-[10px] text-zinc-500">via {st.via}</div>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.api_token ? SOURCE.own.cls : SOURCE.missing.cls}`}>
                      {f.api_token ? "Connected" : "Not set up"}
                    </span>
                    {!f.api_token && <div className="mt-1 text-[10px] text-zinc-500">Settings → Billing details</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => setEditing(f)} data-testid={`ghl-edit-${f.id}`}>
                      <Link2 className="mr-1 h-3.5 w-3.5" /> {f.location_id ? "Edit" : "Connect"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <ConnectionDialog facility={editing} purposes={data.purposes}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ConnectionDialog({ facility, purposes, onClose, onSaved }) {
  const [locationId, setLocationId] = useState(facility.location_id || "");
  const [hooks, setHooks] = useState({});      // new URLs typed in (blank = leave as is)
  const [removed, setRemoved] = useState({});  // webhooks the admin chose to remove
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (locationId.trim() !== (facility.location_id || "")) body.location_id = locationId.trim();
      const webhooks = {
        ...Object.fromEntries(Object.keys(removed).filter((k) => removed[k]).map((k) => [k, ""])),
        ...Object.fromEntries(Object.entries(hooks).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])),
      };
      if (Object.keys(webhooks).length) body.webhooks = webhooks;
      if (!Object.keys(body).length) { onClose(); return; }
      await updateGhlConnection(facility.id, body);
      toast.success(`${facility.name} saved`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#0f0f0f] border-[#262626] text-zinc-100" data-testid="ghl-dialog">
        <DialogHeader>
          <DialogTitle>{facility.name} · GoHighLevel</DialogTitle>
          <DialogDescription className="text-zinc-400">
            The location ID is in the gym&apos;s GHL address: app.gohighlevel.com/v2/location/<b>&lt;ID&gt;</b>/…
            Webhook URLs come from each workflow&apos;s Inbound Webhook trigger.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs text-zinc-400" htmlFor="ghl-loc">GHL location ID</label>
            <Input id="ghl-loc" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="20 characters, e.g. kg9psHvpnMAlx6FTMgv5"
              className="font-mono bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid="ghl-location-input" />
          </div>
          {purposes.map((p) => {
            const st = facility.purposes[p.key];
            return (
              <div key={p.key} className="space-y-1">
                <label className="text-xs text-zinc-400" htmlFor={`ghl-${p.key}`}>{p.label} webhook</label>
                <Input id={`ghl-${p.key}`} value={hooks[p.key] ?? ""} onChange={(e) => setHooks({ ...hooks, [p.key]: e.target.value })}
                  placeholder={st.source === "own" ? `Set (${st.webhook}). Paste a new URL to replace it.` : "https://services.leadconnectorhq.com/hooks/…/webhook-trigger/…"}
                  className="font-mono text-xs bg-[#0b0b0b] border-[#262626] text-zinc-100" data-testid={`ghl-hook-${p.key}`} />
                {st.source === "own" && (
                  <label className="flex items-center gap-2 text-[11px] text-zinc-500">
                    <input type="checkbox" checked={!!removed[p.key]} onChange={(e) => setRemoved({ ...removed, [p.key]: e.target.checked })} />
                    Remove this webhook
                  </label>
                )}
                {p.fallback && st.source !== "own" && <div className="text-[11px] text-zinc-500">Until set, this uses a shared workflow.</div>}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ghl-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
